import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, Modal, Select, Spinner } from '../common';
import RepoPicker, { RepoPickerOption } from '../github/RepoPicker';
import {
  getGraphQLClient,
  GET_GITHUB_CONNECTION,
  GET_GITHUB_REPOS,
  START_GITHUB_OAUTH,
} from '../../lib/graphql';
import {
  useCreateTranslationProject,
  useImportFromGithub,
  useUploadEpub,
} from '../../hooks/useTranslationProjects';

interface GithubConnection {
  githubConnected: boolean;
  githubLogin: string | null;
}

interface BookOption {
  id: string;
  title?: string | null;
  fullTitle?: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  books: BookOption[];
}

type Source = 'book' | 'epub' | 'github';

// Modal to start a new translation from one of three sources. On success it
// navigates straight into the reader for the new project.
export default function CreateTranslationModal({ isOpen, onClose, books }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const create = useCreateTranslationProject();
  const upload = useUploadEpub();
  const importRepo = useImportFromGithub();

  const [source, setSource] = useState<Source>('book');
  const [bookId, setBookId] = useState('');
  const [repoFullName, setRepoFullName] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('lt');
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // GitHub repo picker (loaded lazily when the GitHub tab is opened).
  const [connection, setConnection] = useState<GithubConnection | null>(null);
  const [repos, setRepos] = useState<RepoPickerOption[] | null>(null);

  useEffect(() => {
    if (source !== 'github' || connection !== null) return;
    getGraphQLClient()
      .request<{ me: GithubConnection | null }>(GET_GITHUB_CONNECTION)
      .then((d) => setConnection(d.me ?? { githubConnected: false, githubLogin: null }))
      .catch(() => setConnection({ githubConnected: false, githubLogin: null }));
  }, [source, connection]);

  useEffect(() => {
    if (source !== 'github' || !connection?.githubConnected || repos !== null) return;
    getGraphQLClient()
      .request<{ githubRepos: RepoPickerOption[] }>(GET_GITHUB_REPOS)
      .then((d) => setRepos(d.githubRepos))
      .catch(() => setRepos([]));
  }, [source, connection, repos]);

  const connectGithub = async () => {
    const d = await getGraphQLClient().request<{
      startGithubOauth: { authorizeUrl: string | null; errors: string[] };
    }>(START_GITHUB_OAUTH, { returnTo: '/books?tab=translations' });
    if (d.startGithubOauth.authorizeUrl) window.location.href = d.startGithubOauth.authorizeUrl;
    else setError(d.startGithubOauth.errors.join(', '));
  };

  const busy = create.isPending || upload.isPending || importRepo.isPending;

  const go = (id: string) => {
    onClose();
    navigate(`/translate/${id}`);
  };

  const submit = async () => {
    setError(null);
    try {
      if (source === 'book') {
        const id = bookId || books[0]?.id;
        if (!id) return setError(t('books.translationSourceBook'));
        const r = await create.mutateAsync({ sourceBookId: id, targetLanguage });
        return r.success && r.project ? go(r.project.id) : setError(r.errors.join(', '));
      }
      if (source === 'epub') {
        const file = fileRef.current?.files?.[0];
        if (!file) return setError('EPUB');
        return go(await upload.mutateAsync({ file, targetLanguage }));
      }
      if (!repoFullName) return setError(t('books.translationImportRepo'));
      // Import targets the repo by name under the connected account.
      const r = await importRepo.mutateAsync(repoFullName.split('/').pop() as string);
      return r.success && r.project ? go(r.project.id) : setError(r.errors.join(', '));
    } catch (e) {
      return setError(e instanceof Error ? e.message : 'Error');
    }
  };

  const tabs: { key: Source; label: string }[] = [
    { key: 'book', label: t('books.translationFromBook') },
    { key: 'epub', label: t('books.translationUploadEpub') },
    { key: 'github', label: t('books.translationImportRepo') },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('books.addTranslation')} size="lg">
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 text-sm">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setSource(tab.key)}
              className={`rounded-full px-3 py-1 ${
                source === tab.key ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {source === 'book' && (
          <Select
            label={t('books.translationSourceBook')}
            value={bookId}
            onChange={(e) => setBookId(e.target.value)}
            placeholder="—"
            options={books.map((b) => ({ value: b.id, label: b.fullTitle || b.title || b.id }))}
          />
        )}

        {source === 'epub' && (
          <div>
            <label className="mb-1 block text-sm text-gray-500">{t('books.translationUploadEpub')}</label>
            <input ref={fileRef} type="file" accept=".epub,application/epub+zip" className="text-sm" />
          </div>
        )}

        {source === 'github' && (
          <div>
            {connection === null || (connection.githubConnected && repos === null) ? (
              <div className="flex justify-center py-6"><Spinner /></div>
            ) : !connection.githubConnected ? (
              <div className="rounded-lg border border-gray-200 p-4 text-center dark:border-gray-700">
                <p className="mb-3 text-sm text-gray-500">
                  {t('githubSync.connectAccountDescription', 'Connect your GitHub account to import a repository.')}
                </p>
                <Button onClick={connectGithub}>{t('githubSync.connect', 'Connect GitHub')}</Button>
              </div>
            ) : (
              <RepoPicker
                repos={repos ?? []}
                value={repoFullName}
                onChange={setRepoFullName}
                viewerLogin={connection.githubLogin}
              />
            )}
          </div>
        )}

        {source !== 'github' && (
          <Input
            label={t('books.translationTargetLanguage')}
            value={targetLanguage}
            onChange={(e) => setTargetLanguage(e.target.value)}
            placeholder="lt"
          />
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? t('common.loading', 'Working…') : t('books.translationCreate')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
